<script setup>
import {computed, reactive, shallowRef, watchEffect, ref} from 'vue';
import SquashLevelsChip from "~/components/squashLevels/SquashLevelsChip.vue";
import AvatarImage from "~/components/images/AvatarImage.vue";
import PlayerDialog from "~/components/players/PlayerDialog.vue";
import {playerInitials} from "~/utils/playerInitials";

const props = defineProps({
  id: String,
  player: {
    type: Object,
    default: () => null,
  },
  size: {
    type: String,
    default: 'small',
  },
  expandable: {
    type: Boolean,
    default: true,
  },
  // Off unless a competition surface asks for it. Default-true meant ratings
  // appeared on club-wide lists and non-squash contexts, since nothing here
  // knows the sport — only the caller does. See useSquashLevels.
  showSquashLevel: {
    type: Boolean,
    default: false,
  },
  inline: {
    type: Boolean,
    default: true,
  },
  minimized: {
    type: Boolean,
    default: false
  },
  highlighted: {
    type: Boolean,
    default: false
  },
  highlightColor : {
    type : String,
    default: 'info'
  },
  // Rendered handicap label ("+20", "Rec 15.3") passed down from TeamCard; null hides the chip.
  handicapLabel: {
    type: String,
    default: null,
  },
  // chip class from useHandicaps().chipClassFor — the one colour rule for handicap chips
  handicapTone: {type: String, default: 'tl-chip--brand'},
  maxLines: {
    type: Number,
    default: null,
  },
  // Label shown on the row instead of the player's own name — a singles team an
  // admin renamed (TeamCard passes the override). The dialog still names the player.
  displayName: {
    type: String,
    default: null,
  },
  // Sparse-row mode: render from the (partial) `player` object, but fetch the
  // full player record only when the dialog is opened — so a standings table
  // of 12 rows costs zero extra requests until someone taps a player.
  fetchOnExpand: {
    type: Boolean,
    default: false,
  },
});

// The players request, built only when this card has to fetch: a card given
// an `id` (no player object) fetches as it renders; a fetch-on-expand card
// fetches on its first tap; a card given a `player` never builds it. A members
// list renders hundreds of cards from player objects, and a useCrudApi each
// (19 refs, 6 computeds) was pure cost (MEMORY_M4, 2026-10-20).
let api = null;
const playersApi = shallowRef(null);
const playersCrud = () => api ??= (playersApi.value = useCrudApi('api/v2/players'));
const item = computed(() => playersApi.value ? playersApi.value.item.value : {});
const hasItem = computed(() => !!playersApi.value && playersApi.value.hasItem.value);
const loading = computed(() => !!playersApi.value && playersApi.value.itemPending.value);
const errors = computed(() => playersApi.value ? playersApi.value.itemErrors.value : {});

watchEffect(() => {
  if (props.id && !props.fetchOnExpand) {
    playersCrud().getItem(props.id);
  }
});
// fetchOnExpand: once the full record arrives it wins over the sparse row.
const currentItem = computed(() =>
    (props.fetchOnExpand && hasItem.value ? item.value : null) || props.player || item.value);
const state = reactive({
  dialog: false,
});
// The player sheet (PlayerDialog) is mounted on the first tap and kept so it
// can close with its transition; until then nothing it needs exists.
const dialogMounted = ref(false);
const handleClick = () => {
  if (!props.expandable) return;
  if (props.fetchOnExpand && !hasItem.value) {
    const playerId = props.id || props.player?.id || props.player?.playerId;
    if (playerId) playersCrud().getItem(playerId);
  }
  dialogMounted.value = true;
  state.dialog = true;
};

// X81 (R28 P4): a row that holds the card can open the dialog from a click anywhere on it.
defineExpose({open: handleClick});

const style = computed(() => {
  return {
    cursor: props.expandable ? 'pointer' : 'default',
  };
});
const nameStyle = computed(() => {
  return {
    fontSize: props.size === 'small' ? '14px' : (props.size === 'medium' ? '18px' : '24px'),
    fontWeight: 600,
  };
});
// Avatar should sit just slightly taller than the name text (≈1.3× the font
// size), not dominate it: small 14px→20, medium 18px→24, large 24px→32.
const avatarSize = computed(() => {
  return props.size === 'small' ? 20 : (props.size === 'medium' ? 24 : 32);
});
const avatarClasses = computed(() => {
  return {
    'mr-1': !props.minimized && props.size === 'small',
    'mr-2': !props.minimized && props.size !== 'small'
  }
});
const nameClampClass = computed(() => {
  if (!props.maxLines) return null;
  if (props.maxLines === 1) return 'text-truncate';
  return `line-clamp-${props.maxLines}`;
});
</script>

<template>
  <div :style="style" v-if="currentItem" @click="handleClick" :class="inline ? 'flex-wrap d-flex pc-row' :''">
    <div class="d-flex align-center pc-id">
      <v-badge icon="mdi-arrow-bottom-left" :model-value="highlighted" :color="highlightColor" :floating="true">
        <v-tooltip :disabled="!minimized" :text="displayName || currentItem.fullName" location="bottom">
          <template v-slot:activator="{ props }">
            <avatar-image v-bind="props" :size="avatarSize" :class="avatarClasses" :filename="currentItem.imageFileName"
                          :fallback="playerInitials(currentItem)"/>
            <!-- flex-wrap: the handicap chip drops under the name when the column is tight (phones, matrices) -->
            <div v-if="!minimized" class="align-center d-flex flex-wrap name-row" :style="nameStyle">
              <span :class="nameClampClass">{{ displayName || currentItem.fullName }}</span>
              <span v-if="handicapLabel && !minimized" class="tl-chip pc-hcp" :class="handicapTone">{{ handicapLabel }}</span>
              <!-- Squash Levels: what the API sent (level, or the greyed chip for a non-sharer) -->
              <squash-levels-chip v-if="size === 'medium' || size === 'large'" class="ml-2"
                                  :person="currentItem" :allowed="showSquashLevel"/>
            </div>
          </template>
        </v-tooltip>
      </v-badge>
    </div>
    <squash-levels-chip v-if="size === 'small'" class="ml-2" :person="currentItem" :allowed="showSquashLevel"/>
  </div>
  <div v-else-if="loading">
    Loading...
  </div>
  <div v-else-if="errors.length">
    <crud-error-display :errors="errors"/>
  </div>
  <player-dialog v-if="dialogMounted" v-model="state.dialog" :player="currentItem"/>
</template>

<style scoped>
/* Line clamp styles for multi-line text truncation with ellipsis */
.line-clamp-2,
.line-clamp-3,
.line-clamp-4,
.line-clamp-5 {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.line-clamp-2 { -webkit-line-clamp: 2; }
.line-clamp-3 { -webkit-line-clamp: 3; }
.line-clamp-4 { -webkit-line-clamp: 4; }
.line-clamp-5 { -webkit-line-clamp: 5; }
.pc-hcp {
  font-size: 10px;
  font-weight: 700;
  padding: 0 6px;
  border-radius: 999px;
  white-space: nowrap;
  flex-shrink: 0;
}

/* A long name truncates (maxLines) instead of pushing the row sideways on a
   phone: every box between the row and the name may shrink (3 Nov 2026,
   checked at 320px with a chip beside it). */
.pc-id { min-width: 0; max-width: 100%; }
/* A chip that drops under the name sits 4px below it (owner, 3 Nov 2026). */
.pc-row {
  row-gap: 4px;
}
.pc-id :deep(.v-badge), .pc-id :deep(.v-badge__wrapper) { min-width: 0; max-width: 100%; }
.name-row { min-width: 0; }

/* the name row: an 8px gap beside the name while the chip fits on the line, a 4px gap above it
   once the column is too tight and it wraps — the gap follows the wrap, not the screen width */
.name-row {
  column-gap: 8px;
  row-gap: 4px;
}
</style>
